import {
  isPrefilledWeightSource,
  toCanonicalKg,
  type Dropset as DropsetT,
  type Side,
  type WeightSource,
  type WorkoutSet as WorkoutSetT,
  parseLocaleNumber,
} from '@heraion/shared';
import type { DraftSet } from './state/loggerDraftTypes';
import { draftDropsetRounds } from './dropsetDraft';

// The dropset builder and the two per-set field helpers, split out of
// workoutPayload.ts (SP-WORKOUTPAYLOAD, which was 454 lines). All three are
// pure DraftSet -> field projections with no React and no draft-wide state.
//
// `SetCommon` moved here with them rather than staying behind and being
// exported back. Both files use it, so either direction works — but keeping
// it here makes the dependency one-way for TYPES as well as values
// (workoutPayload -> buildDropsetSet), instead of a type-only cycle. The
// build-time result is the same; the import graph is easier to read.

// Per-set fields shared by every WorkoutSet kind, assembled once from the
// draft set's completion/notes/effort state.
export type SetCommon = {
  completedAt?: string;
  fail?: true;
  note?: string;
  rpe?: number;
  rir?: number;
};

// Carry the prefilled-weight provenance from the draft set onto the
// persisted weighted/bodyweight set (→ LogSet.weightSource) for the
// post-session deviation math. Data-only, no UI. Persist only when it
// carries signal: 'manual' (and absent) are equivalent by LogSet
// convention, so omit them — same omit-the-default rule as loadedSides=1.
export const weightSourceField = (s: DraftSet): { weightSource?: WeightSource } =>
  isPrefilledWeightSource(s.weightSource)
    ? { weightSource: s.weightSource }
    : {};

// Carry "this set went to the limit on the open variable" onto the
// persisted set (→ LogSet.amrap). Deliberately NOT in SetCommon: common
// is spread onto cardio + timed too, and those WorkoutSet variants don't
// declare `amrap` — an unknown key there fails the NewWorkout parse and
// would reject the whole save. The flag is wired for strength (weighted /
// bodyweight) only in this slice; when the hold/cardio forms are decided
// it's THIS field that extends, never a second one. Omit-the-default like
// weightSource: only `true` carries signal, absent reads as non-AMRAP, so
// legacy rows and plain sets stay byte-identical on the wire.
// #353: the calibration flag rides the same way (weighted only).
export const calibrationField = (s: DraftSet): { calibration?: true } =>
  s.calibration === true ? { calibration: true } : {};

export const amrapField = (s: DraftSet): { amrap?: true } =>
  s.amrap === true ? { amrap: true } : {};

// #75: the set's side (-> LogSet.side), weighted and bodyweight only like
// amrap. Absent: nothing said, the wire stays as it was.
export const sideField = (s: DraftSet): { side?: Side } =>
  s.side ? { side: s.side } : {};

// Carry the SECONDS a reps set was measured in (HI-1 / his report 2026-08-25).
// A plan can bound any set in time — a back squat prescribed 30s is still a
// reps-logging lift — and when the bout hits zero the clock writes those
// seconds into the draft. They used to die right here: buildPayload took the
// reps branch, which had nowhere to put a duration, so the athlete watched the
// hourglass count and then found no time anywhere. «Il field del tempo non
// esiste e quindi non lo mostra da nessuna parte» — and it was worse than not
// showing: it was not SAVED.
//
// ⭐ Nothing at the contract had to move: WorkoutSetWeighted and
// WorkoutSetBodyweight have declared an optional `duration` since 1.62.0
// (schemas/workout.ts). The model always allowed reps × weight × time; only
// the client insisted on choosing.
//
// Omit-the-default like its two siblings: 0 and an unparseable cell carry no
// measurement, so they stay off the wire and legacy sets are byte-identical.
export const measuredDurationField = (s: DraftSet): { duration?: number } => {
  const seconds = parseInt(s.duration, 10);
  return Number.isFinite(seconds) && seconds > 0 ? { duration: seconds } : {};
};

// Assemble a dropset/myorep set into its persisted WorkoutSet. The TOP
// round becomes the parent set (reps + weight/extra); the remaining
// rounds become the canonical Dropset chain. Which round is the top is
// resolved by draftDropsetRounds (shared with live totals), so an empty
// parent rep count never drops the set — the data lives in the chain.
// Returns null only when there is no usable top round (no reps, or a
// weighted top with no weight — an incomplete set the schema can't hold).
export const buildDropsetSet = (
  s: DraftSet,
  isBodyweight: boolean,
  common: SetCommon,
): WorkoutSetT | null => {
  const rounds = draftDropsetRounds(s);
  const top = rounds[0];
  if (!top) return null;
  const topReps = parseInt(top.reps, 10);
  if (!Number.isFinite(topReps) || topReps < 0) return null;
  const variant = s.dropset?.variant ?? 'classical';

  const steps: DropsetT['steps'] = [];
  for (let i = 1; i < rounds.length; i++) {
    const d = rounds[i]!;
    const r = parseInt(d.reps, 10);
    if (!Number.isFinite(r) || r <= 0) continue;
    const w = d.weight.trim() === '' ? null : parseLocaleNumber(d.weight);
    const stepCommon = d.completedAt ? { completedAt: d.completedAt } : {};
    if (isBodyweight) {
      if (w !== null && Number.isFinite(w)) {
        steps.push({ reps: r, extra: toCanonicalKg(w, s.unit), ...stepCommon });
      } else {
        steps.push({ reps: r, ...stepCommon });
      }
    } else if (w !== null && Number.isFinite(w) && w >= 0) {
      steps.push({ reps: r, weight: toCanonicalKg(w, s.unit), ...stepCommon });
    } else {
      steps.push({ reps: r, ...stepCommon });
    }
  }
  const dropset: DropsetT | undefined =
    steps.length > 0 ? { variant, steps } : undefined;

  if (isBodyweight) {
    const extraRaw = top.weight.trim();
    const extraAbs = extraRaw === '' ? null : parseLocaleNumber(extraRaw);
    const bwSides = { loadedSides: s.loadedSides };
    if (extraAbs !== null && Number.isFinite(extraAbs)) {
      const signed = top.extraNegative ? -Math.abs(extraAbs) : extraAbs;
      return {
        type: 'bodyweight',
        reps: topReps,
        extra: toCanonicalKg(signed, s.unit),
        ...bwSides,
        ...common,
        ...weightSourceField(s),
        ...amrapField(s),
        ...sideField(s),
        ...(dropset ? { dropset } : {}),
      };
    }
    return {
      type: 'bodyweight',
      reps: topReps,
      ...bwSides,
      ...common,
      ...weightSourceField(s),
      ...amrapField(s),
      ...sideField(s),
      ...(dropset ? { dropset } : {}),
    };
  }
  const weight = parseLocaleNumber(top.weight);
  if (!Number.isFinite(weight) || weight < 0) return null;
  return {
    type: 'weighted',
    reps: topReps,
    weight: toCanonicalKg(weight, s.unit),
    loadedSides: s.loadedSides,
    ...common,
    ...weightSourceField(s),
    ...amrapField(s),
    ...calibrationField(s),
    ...sideField(s),
    ...(dropset ? { dropset } : {}),
  };
};
